/**
 * The shared look of a text control, in one place.
 *
 * `Input`, `Textarea` and `Select` are deliberately dumb — `Field` owns
 * the label, the hint, the error text and the `aria-describedby` wiring,
 * and the controls own nothing but their own box. This module is the box.
 *
 * The invalid border is derived from the `aria-invalid` the control was
 * handed rather than from a separate `isInvalid` prop, so the visual state
 * and the announced state cannot disagree: there is only one input. It is
 * computed in TS rather than through an `aria-[invalid=true]:` variant
 * because a variant that silently fails to compile is indistinguishable
 * from a correct one until someone looks at a form with an error in it.
 */

/** React's `aria-invalid` accepts a boolean, the strings `'true'`/`'false'`
 * and the grammar tokens. Anything that is not an explicit negative counts
 * as invalid, which matches what assistive tech does with it. */
export function isAriaInvalid(value: unknown): boolean {
  return value !== undefined && value !== false && value !== 'false'
}

export interface ControlClassOptions {
  isInvalid?: boolean
  /** Machine input — a node path, an argv pattern, a key name. Same
   * reasoning as `Code`: these are compared character by character. */
  isMono?: boolean
}

/** `bg-sunken` rather than `bg-surface`: an input is a well cut into the
 * card, which is the only elevation cue that works on a near-black theme
 * where a drop shadow is invisible. */
const BASE =
  'w-full rounded-md border bg-sunken px-2 text-fg transition-colors placeholder:text-faint disabled:cursor-not-allowed disabled:opacity-50'

export function controlClassName({
  isInvalid = false,
  isMono = false,
}: ControlClassOptions = {}): string {
  // `border-strong` on hover is the same 3:1-rated edge the active state
  // uses; the accent is reserved for focus, which the global
  // `:focus-visible` ring in `styles.css` already paints.
  const border = isInvalid
    ? 'border-status-danger-border'
    : 'border-border hover:border-strong'
  return `${BASE} ${border} ${isMono ? 'font-mono text-xs' : 'text-sm'}`
}
